import { createAuthClient } from "better-auth/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useLocation } from "wouter";
import { Sentry } from "@/lib/sentry";
import { toast } from "sonner";

/**
 * Better Auth client. The API server mounts Better Auth at /api/auth on
 * the same origin, so cookies flow automatically.
 */
export const authClient = createAuthClient({
  baseURL: typeof window !== "undefined" ? window.location.origin : undefined,
  basePath: "/api/auth",
});

export const MIN_PASSWORD_LENGTH = 12;

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

/** Absolute URL inside this app (respects the Vite base path). */
export function appUrl(path: string): string {
  return `${window.location.origin}${basePath}${path}`;
}

type AuthErrorLike = { code?: string; message?: string; status?: number; statusText?: string } | null | undefined;

const FRIENDLY: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: "That email and password combination is not right.",
  EMAIL_NOT_VERIFIED: "Verify your email before signing in. Check your inbox for the link.",
  USER_ALREADY_EXISTS: "An account with this email already exists. Sign in or reset your password.",
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: "An account with this email already exists. Sign in or reset your password.",
  PASSWORD_TOO_SHORT: `Passwords must be at least ${MIN_PASSWORD_LENGTH} characters.`,
  PASSWORD_TOO_LONG: "That password is too long.",
  INVALID_PASSWORD: "Your current password is incorrect.",
  INVALID_TOKEN: "This link is invalid or has expired. Request a new one.",
  INVALID_EMAIL: "Enter a valid email address.",
  CREDENTIAL_ACCOUNT_NOT_FOUND: "This account has no password yet. Use “Forgot password” to set one.",
};

export function authErrorMessage(err: AuthErrorLike, fallback = "Something went wrong. Please try again."): string {
  if (!err) return fallback;
  if (err.code && FRIENDLY[err.code]) return FRIENDLY[err.code];
  if (err.status === 429) return "Too many attempts. Wait a moment and try again.";
  if (err.status === 404 && !err.message) return "Authentication is not available right now.";
  return err.message || err.statusText || fallback;
}

/** Signs out, clears every cached query, and returns to sign-in. */
export function useSignOut() {
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  return useCallback(async () => {
    try {
      const result = await authClient.signOut();
      if (result.error) {
        toast.error(authErrorMessage(result.error, "Could not sign out. Please try again."));
        return;
      }
      queryClient.clear();
      Sentry.setUser(null);
      setLocation("/sign-in");
    } catch {
      toast.error("Could not sign out. Check your connection and try again.");
    }
  }, [queryClient, setLocation]);
}
